import React from 'react';
import { useFetch, fmtDateTime, statusStyles } from '../utils';
import { adminApi } from '../api';
import { Section } from '../_primitives/Section';
import { DataTable, type Column } from '../_primitives/DataTable';
import type { EmailLogRow, ActivityResponse } from '../types';

const emailCols: Column<EmailLogRow>[] = [
  { key: 'created_at', label: 'Verzonden', sortValue: (e) => e.created_at,
    render: (e) => <span className="text-ink/55 dark:text-white/55">{fmtDateTime(e.created_at)}</span> },
  { key: 'to_email', label: 'Naar', sortValue: (e) => e.to_email, searchValue: (e) => e.to_email,
    render: (e) => <span className="text-ink/85 dark:text-white/85">{e.to_email}</span> },
  { key: 'subject', label: 'Onderwerp', searchValue: (e) => e.subject,
    render: (e) => <span className="text-ink/75 dark:text-white/75 truncate max-w-[36ch] inline-block" title={e.subject}>{e.subject}</span> },
  { key: 'template', label: 'Template', sortValue: (e) => e.template ?? '',
    render: (e) => <span className="text-ink/55 dark:text-white/55">{e.template ?? '—'}</span> },
  { key: 'status', label: 'Status',
    render: (e) => <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-md ${statusStyles(e.status)}`}>{e.status}</span> },
];

const loginCols: Column<ActivityResponse['recentLogins'][number]>[] = [
  { key: 'last_login', label: 'Tijdstip', sortValue: (l) => l.last_login,
    render: (l) => <span className="text-ink/55 dark:text-white/55">{fmtDateTime(l.last_login)}</span> },
  { key: 'email', label: 'Email', sortValue: (l) => l.email, searchValue: (l) => l.email,
    render: (l) => <span className="font-medium text-ink dark:text-white">{l.email}</span> },
  { key: 'first_name', label: 'Naam', searchValue: (l) => l.first_name ?? '',
    render: (l) => <span className="text-ink/65 dark:text-white/65">{l.first_name ?? '—'}</span> },
  { key: 'company_name', label: 'Bedrijf', searchValue: (l) => l.company_name ?? '',
    render: (l) => <span className="text-ink/65 dark:text-white/65">{l.company_name ?? '—'}</span> },
  { key: 'role', label: 'Rol', sortValue: (l) => l.role,
    render: (l) => <span className="text-ink/65 dark:text-white/65">{l.role}</span> },
  { key: 'login_count', label: 'Logins', align: 'right', sortValue: (l) => l.login_count,
    render: (l) => <span className="font-mono text-ink/55 dark:text-white/55">{l.login_count}</span> },
];

const renderActivityCols: Column<ActivityResponse['recentRenders'][number]>[] = [
  { key: 'created_at', label: 'Tijdstip', sortValue: (r) => r.created_at,
    render: (r) => <span className="text-ink/55 dark:text-white/55">{fmtDateTime(r.created_at)}</span> },
  { key: 'email', label: 'Gebruiker', sortValue: (r) => r.email ?? '', searchValue: (r) => r.email ?? '',
    render: (r) => <span className="text-ink/85 dark:text-white/85">{r.email ?? '—'}</span> },
  { key: 'mode', label: 'Mode', sortValue: (r) => r.mode ?? '',
    render: (r) => <span className="text-ink/65 dark:text-white/65">{r.mode ?? '—'}</span> },
  { key: 'room_type', label: 'Ruimte', sortValue: (r) => r.room_type ?? '',
    render: (r) => <span className="text-ink/65 dark:text-white/65">{r.room_type ?? '—'}</span> },
  { key: 'generation_type', label: 'Type', sortValue: (r) => r.generation_type ?? '',
    render: (r) => <span className="text-ink/65 dark:text-white/65">{r.generation_type ?? '—'}</span> },
];

export const LogsTab: React.FC = () => {
  const emails = useFetch(adminApi.emailLogs);
  const activity = useFetch(adminApi.activity);

  return (
    <div className="space-y-4">
      <Section title={`Email logs (${(emails.data ?? []).length})`} subtitle="Outbound deliverability">
        <DataTable
          rows={emails.data ?? []}
          loading={emails.loading}
          error={emails.error}
          onRetry={emails.refresh}
          columns={emailCols}
          rowKey={(e) => e.id}
          searchPlaceholder="Zoek op ontvanger of onderwerp…"
        />
      </Section>

      <Section title="Audit — recente logins" subtitle="Laatste 50">
        <DataTable
          rows={activity.data?.recentLogins ?? []}
          loading={activity.loading}
          error={activity.error}
          onRetry={activity.refresh}
          columns={loginCols}
          rowKey={(l) => l.id}
          searchPlaceholder="Zoek op email of naam…"
        />
      </Section>

      <Section title="Audit — recente renders" subtitle="Laatste 50">
        <DataTable
          rows={activity.data?.recentRenders ?? []}
          loading={activity.loading}
          error={activity.error}
          onRetry={activity.refresh}
          columns={renderActivityCols}
          rowKey={(r) => r.id}
          searchPlaceholder="Zoek op gebruiker, mode, ruimte…"
        />
      </Section>
    </div>
  );
};
